// src/pages/irinox/IrinoxAnalyticsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { dashboardApi } from "../../api/dashboardApi";
import type { MachineChartsResponse } from "../../api/machineChartsApi";
import { WidgetChart } from "../../components/charts/WidgetChart";
import { useAuth } from "../../auth/AuthContext";

function toLocalInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v: string) {
  return new Date(v);
}

type IrinoxDetail = {
  stato: { online: boolean; cycleRunning: number; type: string; domain: string };
  info: Record<string, unknown>;
  alarms: Array<{ name: string; timestamp: number; active: boolean }>;
  status: Record<string, unknown>;
  deviceTwin: { info: any; status: any } | null;
};

type EnergySeries = { label: string; data: number[] };

export default function IrinoxAnalyticsPage() {
  const params = useParams();
  const { token } = useAuth() as { token: string | null };
  const machineId = (params as any).machineId as string | undefined;

  const [fromLocal, setFromLocal] = useState(() => {
    const d = new Date(Date.now() - 7 * 24 * 3600 * 1000);
    return toLocalInputValue(d);
  });
  const [toLocal, setToLocal] = useState(() => toLocalInputValue(new Date()));

  const [chartsData, setChartsData] = useState<MachineChartsResponse | null>(null);
  const [detail, setDetail] = useState<IrinoxDetail | null>(null);
  const [energyData, setEnergyData] = useState<{
    series: EnergySeries[];
    labels: string[];
  } | null>(null);
  const [stateWidget, setStateWidget] = useState<import("../../api/types").ChartWidget | null>(null);
  const [stateByDayWidget, setStateByDayWidget] = useState<import("../../api/types").ChartWidget | null>(null);
  const [cycleWidget, setCycleWidget] = useState<import("../../api/types").ChartWidget | null>(null);
  const [cycleByDayWidget, setCycleByDayWidget] = useState<import("../../api/types").ChartWidget | null>(null);
  const [tCellWidget, setTCellWidget] = useState<import("../../api/types").ChartWidget | null>(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fromISO = useMemo(() => fromLocalInputValue(fromLocal).toISOString(), [fromLocal]);
  const toISO = useMemo(() => fromLocalInputValue(toLocal).toISOString(), [toLocal]);

  // Timestamp inizio giornata per energy chart (oggi o data "da")
  const energyStartTs = useMemo(() => {
    const d = fromLocalInputValue(fromLocal);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [fromLocal]);

  async function load() {
    if (!machineId) {
      setErr("machineId mancante nella route.");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const [chartsRes, detailRes, energyRes, stateRes, state2Res, cycleRes, cycle2Res, tCellRes] =
        await Promise.allSettled([
        dashboardApi.getMachineCharts(token, machineId, fromISO, toISO),
        dashboardApi.getIrinoxMachineDetail(token, machineId),
        dashboardApi.getIrinoxEnergyChart(token, machineId, energyStartTs),
        dashboardApi.getIrinoxAdminChart(token, machineId, "state_1", fromISO, toISO),
        dashboardApi.getIrinoxAdminChart(token, machineId, "state_2", fromISO, toISO),
        dashboardApi.getIrinoxAdminChart(token, machineId, "cycle_1", fromISO, toISO),
        dashboardApi.getIrinoxAdminChart(token, machineId, "cycle_2", fromISO, toISO),
        dashboardApi.getIrinoxAdminChart(token, machineId, "t_cell", fromISO, toISO),
      ]);

      if (chartsRes.status === "fulfilled") setChartsData(chartsRes.value);
      else setChartsData(null);

      if (detailRes.status === "fulfilled") setDetail(detailRes.value as IrinoxDetail);
      else setDetail(null);

      if (energyRes.status === "fulfilled") setEnergyData(energyRes.value);
      else setEnergyData(null);

      if (stateRes.status === "fulfilled") setStateWidget(stateRes.value);
      else setStateWidget(null);

      if (state2Res.status === "fulfilled") setStateByDayWidget(state2Res.value);
      else setStateByDayWidget(null);

      if (cycleRes.status === "fulfilled") setCycleWidget(cycleRes.value);
      else setCycleWidget(null);

      if (cycle2Res.status === "fulfilled") setCycleByDayWidget(cycle2Res.value);
      else setCycleByDayWidget(null);

      if (tCellRes.status === "fulfilled") setTCellWidget(tCellRes.value);
      else setTCellWidget(null);

      if (
        chartsRes.status === "rejected" &&
        detailRes.status === "rejected" &&
        energyRes.status === "rejected" &&
        stateRes.status === "rejected" &&
        state2Res.status === "rejected" &&
        cycleRes.status === "rejected" &&
        cycle2Res.status === "rejected" &&
        tCellRes.status === "rejected"
      ) {
        setErr(
          chartsRes.reason?.message ??
            detailRes.reason?.message ??
            "Errore caricamento dati"
        );
      }
    } catch (e: any) {
      setErr(e?.message ?? "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId, fromISO, toISO, energyStartTs]);

  const widgets = (chartsData?.widgets ?? []).slice(0, 4);
  const status = detail?.status ?? {};
  const deviceStatus = detail?.deviceTwin?.status ?? {};
  const tCell = status?.TCell ?? deviceStatus?.TCell;
  const setpoint = status?.setPoint ?? deviceStatus?.setPoint ?? status?.TSet;
  const tempVal = typeof tCell === "number" ? (Math.abs(tCell) > 45 ? tCell / 10 : tCell) : null;
  const setpointVal =
    typeof setpoint === "number" ? (Math.abs(setpoint) > 45 ? setpoint / 10 : setpoint) : null;

  // Calcola consumi da energy chart (label API: estimatedConsumption, estimatedExpense)
  const consumptionSeries = energyData?.series?.find(
    (s) =>
      s.label?.toLowerCase().includes("consumption") ||
      s.label?.toLowerCase().includes("consumo") ||
      s.label?.toLowerCase().includes("estimatedconsumption")
  );
  const expenseSeries = energyData?.series?.find(
    (s) =>
      s.label?.toLowerCase().includes("expense") ||
      s.label?.toLowerCase().includes("spesa") ||
      s.label?.toLowerCase().includes("estimatedexpense")
  );
  const totConsumption =
    consumptionSeries?.data?.reduce((a, b) => a + b, 0) ?? null;
  const totExpense = expenseSeries?.data?.reduce((a, b) => a + b, 0) ?? null;
  const instantConsumption =
    consumptionSeries?.data?.length
      ? consumptionSeries.data[consumptionSeries.data.length - 1]
      : null;

  // Dati per grafico energia
  const energyChartRows = useMemo(() => {
    if (!energyData?.labels?.length || !consumptionSeries) return [];
    return energyData.labels.map((lbl, i) => ({
      x: lbl,
      consumo: consumptionSeries.data?.[i] ?? 0,
      spesa: expenseSeries?.data?.[i] ?? 0,
    }));
  }, [energyData, consumptionSeries, expenseSeries]);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h4 className="mb-0">
            <i className="bi bi-graph-up me-2" />
            Irinox Analytics
          </h4>
          <div className="text-muted small">
            Macchina: {machineId ?? "-"} • Dati da FreshCloud
          </div>
        </div>
        <Link to="/machines/IRINOX" className="btn btn-outline-primary btn-sm">
          <i className="bi bi-arrow-left me-1" />
          Torna a Irinox
        </Link>

        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label mb-1 small text-muted">Da</label>
            <input
              className="form-control form-control-sm"
              type="datetime-local"
              value={fromLocal}
              onChange={(e) => setFromLocal(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label mb-1 small text-muted">A</label>
            <input
              className="form-control form-control-sm"
              type="datetime-local"
              value={toLocal}
              onChange={(e) => setToLocal(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Carico...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise me-1" />
                Aggiorna
              </>
            )}
          </button>
        </div>
      </div>

      {err && (
        <div className="alert alert-danger mb-3">
          <i className="bi bi-exclamation-triangle me-2" />
          {err}
        </div>
      )}

      {/* KPI principali */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Temperatura camera</div>
              <div className="fs-4 fw-bold text-primary">
                {tempVal != null ? `${tempVal.toFixed(1)} °C` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Ore lavoro</div>
              <div className="fs-4 fw-bold">
                {setpointVal != null ? `${setpointVal.toFixed(1)} °C` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Consumo energia</div>
              <div className="fs-4 fw-bold text-success">
                {totConsumption != null ? `${totConsumption.toFixed(1)} kWh` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Spesa equivalente</div>
              <div className="fs-4 fw-bold">
                {totExpense != null ? `€ ${totExpense.toFixed(2)}` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Consumo istantaneo</div>
              <div className="fs-4 fw-bold">
                {instantConsumption != null ? `${instantConsumption.toFixed(0)} W` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Media temperatura</div>
              <div className="fs-4 fw-bold">
                {chartsData?.kpi?.avgTemp != null
                  ? `${chartsData.kpi.avgTemp.toFixed(1)} °C`
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stato macchina */}
      {detail && (
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-cpu me-2" />
                Stato macchina
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-auto">
                    <span
                      className={`badge ${
                        detail.stato?.online ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {detail.stato?.online ? "Online" : "Offline"}
                    </span>
                  </div>
                  <div className="col-auto">
                    <span className="text-muted">Ciclo in corso:</span>{" "}
                    {detail.stato?.cycleRunning ?? "—"}
                  </div>
                  <div className="col-auto">
                    <span className="text-muted">Tipo:</span>{" "}
                    {detail.stato?.type ?? "—"}
                  </div>
                  {detail.alarms?.some((a) => a.active) && (
                    <div className="col-auto">
                      <span className="badge bg-danger">
                        {detail.alarms.filter((a) => a.active).length} allarmi attivi
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grafico consumi / spesa energia */}
      {energyChartRows.length > 0 && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex align-items-center justify-content-between">
                  <span>
                    <i className="bi bi-lightning-charge me-2" />
                    Consumo e spesa energia (periodo selezionato)
                  </span>
                </div>
                <div className="card-body" style={{ height: 280, minHeight: 200 }}>
                  <ResponsiveContainer width="100%" height={280} minHeight={200}>
                    <AreaChart data={energyChartRows}>
                      <defs>
                        <linearGradient id="consGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#198754" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#198754" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="spesaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0d6efd" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#0d6efd" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                      <XAxis dataKey="x" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} unit=" kWh" />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit=" €" />
                      <Tooltip
                        formatter={(val: number, name: string) => [
                          name === "consumo" ? `${val.toFixed(1)} kWh` : `€ ${val.toFixed(2)}`,
                          name === "consumo" ? "Consumo" : "Spesa",
                        ]}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="consumo"
                        stroke="#198754"
                        fill="url(#consGrad)"
                        name="consumo"
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="spesa"
                        stroke="#0d6efd"
                        fill="url(#spesaGrad)"
                        name="spesa"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Tabella dettagli consumi (dati del periodo) */}
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex align-items-center justify-content-between">
                  <span>
                    <i className="bi bi-table me-2" />
                    Dettagli consumi (dati del periodo)
                  </span>
                </div>
                <div className="card-body">
                  <div
                    className="table-responsive"
                    style={{ maxHeight: 400, overflowY: "auto" }}
                  >
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>
                        <tr>
                          <th>Nome ciclo</th>
                          <th>Data e ora</th>
                          <th>Funzione</th>
                          <th className="text-end">Consumo stimato (kWh)</th>
                          <th className="text-end">Spesa stimata (€)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {energyChartRows.map((row, idx) => {
                          const baseDate = fromLocal ? String(fromLocal).split("T")[0] : "";
                          const datePart = baseDate
                            ? new Date(baseDate + "T12:00:00").toLocaleDateString("it-IT", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "";
                          const dateTimeStr = datePart && row.x ? `${datePart} ${row.x}` : (row.x ?? "-");
                          const ciclo =
                            detail?.stato?.cycleRunning ??
                            detail?.stato?.type ??
                            "-";
                          const funzione = detail?.stato?.type ?? "-";
                          return (
                            <tr key={`${row.x}-${idx}`}>
                              <td>{ciclo}</td>
                              <td>{dateTimeStr}</td>
                              <td>{funzione}</td>
                              <td className="text-end">
                                {row.consumo != null ? row.consumo.toFixed(2) : "-"}
                              </td>
                              <td className="text-end">
                                {row.spesa != null ? row.spesa.toFixed(2) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Grafici telemetria (temperatura, stati, cicli) */}
      <div className="row g-3">
        {widgets.map((w) => (
          <div key={w.id} className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <WidgetChart
                  widget={w}
                  height={260}
                  mode={w.type === "bar" ? "grouped" : undefined}
                />
              </div>
            </div>
          </div>
        ))}

        {stateWidget && (
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-pie-chart me-2" />
                Stati macchina (FreshCloud)
              </div>
              <div className="card-body">
                <WidgetChart widget={stateWidget} height={260} />
              </div>
            </div>
          </div>
        )}

        {stateByDayWidget && (
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-bar-chart me-2" />
                Stati macchina per giorno (FreshCloud)
              </div>
              <div className="card-body">
                <WidgetChart
                  widget={stateByDayWidget}
                  height={260}
                  mode={stateByDayWidget.type === "bar" ? "stacked" : undefined}
                />
              </div>
            </div>
          </div>
        )}

        {cycleWidget && (
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-pie-chart-fill me-2" />
                Mix cicli (FreshCloud)
              </div>
              <div className="card-body">
                <WidgetChart widget={cycleWidget} height={260} />
              </div>
            </div>
          </div>
        )}

        {cycleByDayWidget && (
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-bar-chart-fill me-2" />
                Cicli per giorno (FreshCloud)
              </div>
              <div className="card-body">
                <WidgetChart
                  widget={cycleByDayWidget}
                  height={260}
                  mode={cycleByDayWidget.type === "bar" ? "stacked" : undefined}
                />
              </div>
            </div>
          </div>
        )}

        {tCellWidget && (
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <i className="bi bi-activity me-2" />
                Temperatura cella nel tempo (FreshCloud - t_cell)
              </div>
              <div className="card-body">
                <WidgetChart widget={tCellWidget} height={260} />
              </div>
            </div>
          </div>
        )}

        {!loading && widgets.length === 0 && !energyChartRows.length && (
          <div className="col-12">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2" />
              Nessun dato disponibile per il periodo selezionato. Verifica che la sync Irinox sia
              attiva e che le credenziali FreshCloud siano corrette.
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
